import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityInterface,
  AutomataService,
  AutomationInterface,
  DataType,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { DeclinedReason } from '@vendasta/sales-orders';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, share, switchMap, take } from 'rxjs/operators';
import { SalesOrdersService } from '../../../../data-services/sales-orders.service';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { buildStepReferenceString, isReferencingAPreviousStep } from '../../common/utils/step-reference';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';

@Injectable({ providedIn: 'root' })
export class DeclineSalesOrderService extends AutomationDisplayService {
  declinedReasonIds$: Observable<DeclinedReason[]>;

  constructor(
    protected readonly translateService: TranslateService,
    private readonly salesOrderService: SalesOrdersService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly partnerId$: Observable<string>,
    private readonly automataService: AutomataService,
  ) {
    super();
    this.declinedReasonIds$ = this.partnerId$.pipe(
      switchMap((partnerId) => this.salesOrderService.getAllDeclinedReasons(partnerId)),
      take(1),
      // 'share' multi-casts the return value to subsequent subscribers
      // so each activity row doesn't have to make a separate api call
      share(),
    );
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(namespace: string, activity: ActivityInterface): ActivityTableEntry {
    // Check for problems creating the tag.
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DECLINE_SALES_ORDER.ACTIVITY.FAILED'),
        this.emptyBody(),
      );
    }
    return this.tableEntry(
      this.getTruncatedDeclinedReasonFromStep(
        activity?.workflowStep,
        'AUTOMATIONS.EDITOR.TASKS.DECLINE_SALES_ORDER.ACTIVITY.TITLE',
        activity?.activityData,
        '',
      ),
      this.emptyBody(),
    );
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
    automation: AutomationInterface,
  ): Observable<NodeData> {
    return this.nodeDisplayData(
      this.getTruncatedDeclinedReasonFromStep(
        step,
        'AUTOMATIONS.EDITOR.TASKS.DECLINE_SALES_ORDER.ACTION.TITLE',
        '',
        automation.id,
      ),
      this.noValidationErrorsDetected(),
    );
  }

  /** Returns a truncated display version of the declined reason from a step, using the activity data
   *   when given as that was used to populate the actual values of the step activity
   * */
  getTruncatedDeclinedReasonFromStep(
    step: WorkflowStepInterface,
    translationKey: string,
    activityData: string,
    automationId: string,
  ): Observable<string> {
    let previousStepsWithOutputParameters$ = of(new Map());
    if (automationId) {
      previousStepsWithOutputParameters$ = this.automataService
        .getWorkflowStepPredecessorOutputParams(automationId, [step.id], true)
        .pipe(
          map((previousWorkflowSteps) => {
            const workflowStepKeyToStepNameMap = new Map();
            for (const previousStep of previousWorkflowSteps) {
              // append previous step key if it has output parameters of the right type and ignoring the current step id
              if (!!previousStep.outputParameters && previousStep.workflowStep.id !== step.id) {
                for (const outputParam of previousStep.outputParameters) {
                  if (outputParam.settings.dataType === DataType.DATA_TYPE_ORDER_DECLINED_REASON_IDS) {
                    const stepKey = buildStepReferenceString(previousStep.workflowStep.id, outputParam.settings.id);
                    workflowStepKeyToStepNameMap.set(stepKey, previousStep.workflowStep.name);
                  }
                }
              }
            }
            return workflowStepKeyToStepNameMap;
          }),
          catchError(() => {
            // This can happen if the current step no longer exists on the automation
            return of(new Map());
          }),
        );
    }
    return combineLatest([this.declinedReasonIds$, previousStepsWithOutputParameters$]).pipe(
      switchMap(([declinedReasonIds, previousStepsWithOutputParameters]) => {
        return this.buildTranslatedReasonFromActivityAndStepData(
          translationKey,
          step,
          activityData,
          declinedReasonIds,
          previousStepsWithOutputParameters,
        );
      }),
    );
  }

  // Builds the message for what reason was given. Prefers values from activityData as the step.data may have template
  // variables that the activityData would have evaluated into actual values.
  // If using the step.data, when the decline_reason_id references a step key, eg. ({.WorkflowStep. ...),
  // then the return string is "from a previous step"
  private buildTranslatedReasonFromActivityAndStepData(
    translationKey: string,
    step: WorkflowStepInterface,
    activityData: string,
    declinedReasons: DeclinedReason[],
    previousStepKeyToNameMap: Map<string, string>,
  ): Observable<string | any> {
    const data = step?.data ? JSON.parse(step.data) : {};
    const dataFromActivity = activityData ? JSON.parse(activityData) : {};

    let declineReasonPreset = '';
    const dr = declinedReasons.find((r) => {
      // attempt to read the declined reason id from the finished activity
      if (dataFromActivity.declined_reason_ids?.length > 0) {
        return r.id === dataFromActivity.declined_reason_ids[0];
      } else if (isReferencingAPreviousStep(data.declined_reason_ids)) {
        return r.id === data.declined_reason_ids[0];
      }
      return false;
    });
    let reasonMessage =
      data.declined_reason_message?.length > 100
        ? data.declined_reason_message.substr(0, 100) + '...'
        : data.declined_reason_message;
    if (!!dataFromActivity.declined_reason_message || !!dataFromActivity.declined_reason_ids) {
      reasonMessage =
        dataFromActivity.declined_reason_message?.length > 100
          ? dataFromActivity.declined_reason_message.substr(0, 100) + '...'
          : dataFromActivity.declined_reason_message;
    }
    if (dr) {
      declineReasonPreset = dr.reason;
      if (reasonMessage) {
        declineReasonPreset = declineReasonPreset + ': ';
      }
    }
    const reason = '<b>' + declineReasonPreset + reasonMessage + '</b>';
    // if there's no activity data (hasn't ran/is a node display) and references a previous step, pull the name of the
    // step from the mapping
    if (!activityData && isReferencingAPreviousStep(data.declined_reason_ids)) {
      const unknownStep = this.translateService.instant('AUTOMATIONS.COMMON.UNKNOWN_STEP');
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DECLINE_SALES_ORDER.ACTION.FROM_PREVIOUS_STEP', {
        previousStep: previousStepKeyToNameMap.get(data.declined_reason_ids) || unknownStep,
      });
    }
    return this.translateService.stream(translationKey, {
      reason: this.translateService.instant(reason),
    });
  }
}
